// src/utils/patternRules.js - Atualizado com novas regras para SICOOB

/**
 * Arquivo que contém as definições de padrões para categorização automática
 * Centraliza as regras de padrões para diferentes bancos
 */

/**
 * Normaliza descrições de transações do SICOOB removendo números e caracteres especiais
 * @param {string} description - Descrição original da transação
 * @param {string} bankName - Nome do banco (para confirmar que é SICOOB)
 * @returns {string} - Descrição normalizada
 */
export const normalizeSicoobDescription = (description, bankName = "") => {
  if (!description) return "";
  
  // Confirmar que é uma transação do SICOOB/SICREDI/COOP
  const isSicoob = 
    bankName.includes("SICOOB") || 
    bankName.includes("COOP DE CRED") ||
    bankName.includes("COOP") ||
    description.toUpperCase().includes("SICOOB") || 
    description.toUpperCase().includes("SICREDI");
  
  if (!isSicoob) return description.trim().toLowerCase();
  
  // Para SICOOB/COOP, aplicar normalização específica:
  
  // 1. Extrair o nome do estabelecimento/beneficiário para transações comuns
  let normalized = description;
  
  // Tentar extrair o nome de estabelecimento para transações de cartão
  if (normalized.toUpperCase().includes("COMPRAS NACIONAIS")) {
    // Formato: "COMPRAS NACIONAIS-VE0617268 AUTO POSTO SOL DO CURITIBA BR"
    const match = normalized.match(/COMPRAS NACIONAIS-[A-Z0-9]+\s+(.*)/i);
    if (match && match[1]) {
      normalized = match[1].trim();
    }
  } 
  // Tentar extrair o beneficiário para boletos
  else if (normalized.toUpperCase().includes("LIQUIDACAO BOLETO")) {
    // Formato: "LIQUIDACAO BOLETO- 49319411000133 RFG COMERCIO TRANSPORTES E SERVICOS LTDA"
    const match = normalized.match(/LIQUIDACAO BOLETO-\s*\d+\s+(.*)/i);
    if (match && match[1]) {
      normalized = match[1].trim();
    }
  }
  // Tentar extrair o beneficiário para PIX
  else if (normalized.toUpperCase().includes("PAGAMENTO PIX")) {
    // Formato: "PAGAMENTO PIX-PIX_DEB 36958572000125 C2 CAFETERIA LTDA"
    // ou: "PAGAMENTO PIX SICREDI-CX695198 16828505000189 MOKA ASSINATURA DE CAFES ESPECIAIS LTDA"
    const match = normalized.match(/PAGAMENTO PIX[^0-9]*\d+\s+(.*)/i);
    if (match && match[1]) {
      normalized = match[1].trim();
    }
  }
  
  // Tratar o caso específico de "INTEGR.CAPITAL SUBSCRITO-1"
  if (normalized.includes("CAPITAL SUBSCRITO")) {
    return "integralizacao capital";
  }
  
  // 2. Para outros tipos de transações, aplicar a normalização padrão
  if (normalized === description) {
    // Converter para maiúsculas para normalização
    normalized = description.toUpperCase();
  
    // Remover prefixos comuns
    const prefixPatterns = [
      /^SICOOB[_\s]*.*?PIX[_\s]*/,
      /^PAGAMENTO PIX[^a-zA-Z]*/,
      /^LIQUIDACAO BOLETO[^a-zA-Z]*/,
      /^COMPRAS NACIONAIS[^a-zA-Z]*/,
      /^INTEGR\.CAPITAL[^a-zA-Z]*/
    ];
    
    for (const pattern of prefixPatterns) {
      normalized = normalized.replace(pattern, "");
    }
    
    // Remover números e códigos comuns
    normalized = normalized.replace(/\b\d{5,}\b/g, ""); // Remover sequências longas de números
    normalized = normalized.replace(/\b[A-Z]{2,3}\d{4,}\b/g, ""); // Remover códigos como CX695198
    
    // Remover CNPJs e CPFs
    normalized = normalized.replace(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}|\d{3}\.\d{3}\.\d{3}-\d{2}|\b\d{11}\b|\b\d{14}\b/g, "");
  }
  
  // 3. Normalizar cartões de débito, crédito e outras operações comuns
  // Padronizar cartões de débito (remover marca da bandeira)
  if (normalized.toUpperCase().includes("STONE DEB") || normalized.toUpperCase().includes("SUB DB")) {
    // Simplificar cartões de débito para um formato padrão
    normalized = "cartao debito";
  }
  
  // Padronizar cartões de crédito (remover marca da bandeira)
  else if (normalized.toUpperCase().includes("STONE ANTEC") || normalized.toUpperCase().includes("SUB CR")) {
    // Simplificar cartões de crédito para um formato padrão
    normalized = "cartao credito";
  }
  
  // 4. Limpeza final
  // Remover caracteres especiais e espaços extras
  normalized = normalized.replace(/[-_.:;,\/\\(){}[\]]+/g, " ");
  normalized = normalized.replace(/\s+/g, " ").trim();
  
  // 5. Retornar em minúsculas para padronização
  return normalized.toLowerCase();
};

/**
 * Função para extrair o beneficiário de um boleto
 * @param {string} description - Descrição completa da transação
 * @returns {string|null} - Nome do beneficiário ou null se não for encontrado
 */
export const extractBoletoInfo = (description) => {
  if (!description) return null;
  
  // Converter para maiúsculas para normalização
  const upperDesc = description.toUpperCase();
  
  // Verificar se é um boleto
  if (upperDesc.includes("BOLETO") || upperDesc.includes("LIQUIDACAO") || upperDesc.includes("TITULO")) {
    // Padrão para boletos do SICOOB/SICREDI: "LIQUIDACAO BOLETO SICREDI-XXXXXXXX CNPJ BENEFICIARIO"
    if (upperDesc.includes("SICREDI") || upperDesc.includes("SICOOB") || upperDesc.includes("COOP")) {
      // Tentar extrair o CNPJ e o nome do beneficiário
      const match = description.match(/(\d{14}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})\s+(.+)$/);
      
      if (match && match.length >= 3) {
        return {
          cnpj: match[1],
          beneficiary: match[2].trim(),
          type: "BOLETO SICREDI"
        };
      }
      
      // Outro padrão comum: só o nome do beneficiário no final
      const altMatch = description.match(/SICREDI-[\d]+\s+[\d]+\s+(.+)$/);
      if (altMatch && altMatch.length >= 2) {
        return {
          beneficiary: altMatch[1].trim(),
          type: "BOLETO SICREDI"
        };
      }
      
      // Padrão para boletos COOP
      const coopMatch = description.match(/LIQUIDACAO BOLETO-\s*\d+\s+(.+)$/);
      if (coopMatch && coopMatch.length >= 2) {
        return {
          beneficiary: coopMatch[1].trim(),
          type: "BOLETO COOP"
        };
      }
      
      // Usar uma abordagem mais simples para pegar o que vier depois do último número
      // Isso é útil para os casos onde o formato é diferente do esperado
      const simplifiedMatch = description.replace(/.*?(\D+)$/, '$1').trim();
      if (simplifiedMatch && simplifiedMatch.length > 3) {
        return {
          beneficiary: simplifiedMatch,
          type: "BOLETO SICREDI (SIMPLIFIED)"
        };
      }
    }
    
    // Outros padrões de boleto podem ser adicionados aqui
  }
  
  return null;
};

/**
 * Teste se uma transação corresponde a um boleto com um beneficiário específico
 * @param {string} description - Descrição da transação 
 * @param {string} beneficiaryPattern - Padrão do nome do beneficiário a ser verificado
 * @returns {boolean} - Verdadeiro se corresponder
 */
const matchBeneficiary = (description, beneficiaryPattern) => {
  const boletoInfo = extractBoletoInfo(description);
  if (!boletoInfo || !boletoInfo.beneficiary) return false;
  
  return boletoInfo.beneficiary.toUpperCase().includes(beneficiaryPattern.toUpperCase());
};

// Padrões para o banco Stone
export const STONE_PATTERNS = {
    MAQUININHA: {
      key: "padrao_stone_maquininha",
      matcher: (description) => description.endsWith("maquininha"),
      category: "Pix",
      categoryPath: "RECEITA.Pix",
      positiveOnly: true
    },
    IFOOD: {
      key: "padrao_stone_ifood",
      matcher: (description) => description.includes("ifood"),
      category: "Ifood",
      categoryPath: "RECEITA.Ifood",
      positiveOnly: true
    },
    DEBITO: {
      key: "padrao_stone_debito",
      matcher: (description) => description.endsWith("débito"),
      category: "Cartão de Débito", 
      categoryPath: "RECEITA.Cartão de Débito",
      positiveOnly: true
    },
    CREDITO: {
      key: "padrao_stone_credito",
      matcher: (description) => description.includes("crédito"),
      category: "Cartão de Crédito",
      categoryPath: "RECEITA.Cartão de Crédito",
      positiveOnly: true
    },
    TED: {
      key: "padrao_stone_ted",
      matcher: (description) => description.endsWith("ted"),
      category: "TED",
      categoryPath: "RECEITA.TED",
      positiveOnly: true
    },
    VOUCHER: {
      key: "padrao_stone_voucher",
      matcher: (description) => description.startsWith("vr") || description.startsWith("alelo"),
      category: "Voucher",
      categoryPath: "RECEITA.Voucher",
      positiveOnly: true
    }
  };
  
// Padrões para o banco SICOOB/COOP
export const SICOOB_PATTERNS = {
  // Padrões de alta prioridade (verificados primeiro)
  ALELO: {
    key: "padrao_sicoob_alelo",
    matcher: (description) => description.toUpperCase().includes("ALELO"),
    priority: "high",
    category: "Voucher",
    categoryPath: "RECEITA.Voucher",
    positiveOnly: true
  },
  BANCO_VR: {
    key: "padrao_sicoob_banco_vr",
    matcher: (description) => description.toUpperCase().includes("BANCO VR"),
    priority: "high",
    category: "Voucher",
    categoryPath: "RECEITA.Voucher",
    positiveOnly: true
  },
  // Nova regra de alta prioridade para PIX-PIX
  PIX_PIX: {
    key: "padrao_sicoob_pix_pix",
    matcher: (description) => description.toUpperCase().includes("PIX-PIX"),
    priority: "high",
    category: "Pix",
    categoryPath: "RECEITA.Pix",
    positiveOnly: true
  },
  // Nova regra para DEBITO em qualquer parte da descrição
  DEBITO_QUALQUER: {
    key: "padrao_sicoob_debito_qualquer",
    matcher: (description) => description.toUpperCase().includes("DEBITO"),
    category: "Cartão de Débito",
    categoryPath: "RECEITA.Cartão de Débito",
    positiveOnly: true
  },
  // Padrões regulares
  IFOOD: {
    key: "padrao_sicoob_ifood",
    matcher: (description) => description.toUpperCase().includes("IFOOD"),
    category: "Ifood",
    categoryPath: "RECEITA.Ifood",
    positiveOnly: true
  },
  DEBITO: {
    key: "padrao_sicoob_debito",
    // Modificada para usar um padrão mais genérico, ignorando bandeiras específicas
    matcher: (description) => {
      const upper = description.toUpperCase();
      return (upper.includes("DEB") || upper.includes("DB")) && 
             !(upper.includes("ANTEC") || upper.includes("CR"));
    },
    category: "Cartão de Débito",
    categoryPath: "RECEITA.Cartão de Débito",
    positiveOnly: true
  },
  CREDITO: {
    key: "padrao_sicoob_credito",
    // Modificada para usar um padrão mais genérico, ignorando bandeiras específicas
    matcher: (description) => {
      const upper = description.toUpperCase(); 
      return upper.includes("ANTEC") || upper.includes("CR");
    },
    category: "Cartão de Crédito",
    categoryPath: "RECEITA.Cartão de Crédito",
    positiveOnly: true
  },
  PIX: {
    key: "padrao_sicoob_pix",
    matcher: (description) => description.toUpperCase().includes("PIX"),
    category: "Pix",
    categoryPath: "RECEITA.Pix",
    positiveOnly: true
  },
  TED: {
    key: "padrao_sicoob_ted",
    matcher: (description) => description.toUpperCase().includes("TED"),
    category: "TED",
    categoryPath: "RECEITA.TED",
    positiveOnly: true
  }
};
  
/**
 * Retorna todos os padrões definidos para um determinado banco
 * @param {string} bankName - Nome do banco
 * @returns {Object} - Objeto com todos os padrões aplicáveis ao banco
 */
export const getPatternsByBank = (bankName) => {
  if (!bankName) return {};
  
  if (bankName.includes("Stone")) {
    return STONE_PATTERNS;
  } else if (bankName.includes("COOP DE CRED") || bankName.includes("SICOOB") || bankName.includes("COOP")) {
    return SICOOB_PATTERNS;
  }
  
  return {};
};

/**
 * Verifica se uma transação corresponde a um padrão para o banco específico
 * ou a um padrão de beneficiário de boleto
 * @param {string} description - Descrição normalizada da transação 
 * @param {string} bankName - Nome do banco
 * @param {number} amount - Valor da transação (opcional)
 * @param {Object} registeredBeneficiaries - Mapeamento de beneficiários registrados (opcional)
 * @returns {Object|null} - Padrão encontrado ou null
 */
export const matchTransactionPattern = (description, bankName, amount = null, registeredBeneficiaries = {}) => {
  if (!description || !bankName) return null;
  
  // 1. Aplicar normalização especial para SICOOB/COOP
  const isSicoob = 
    bankName.includes("SICOOB") || 
    bankName.includes("COOP DE CRED") ||
    bankName.includes("COOP") ||
    description.toUpperCase().includes("SICOOB") || 
    description.toUpperCase().includes("SICREDI");
  
  const normalizedDescription = isSicoob ? 
    normalizeSicoobDescription(description, bankName) : 
    description.trim().toLowerCase();
  
  // 2. Para transações SICOOB/COOP, verificar se há uma correspondência direta
  // com alguma outra transação SICOOB normalizada
  if (isSicoob && registeredBeneficiaries) {
    // Verificar se há algum mapeamento para esta descrição normalizada
    const sicoobKey = `sicoob_${normalizedDescription}`;
    
    if (registeredBeneficiaries[sicoobKey]) {
      const mapping = registeredBeneficiaries[sicoobKey];
      
      // Verificar o sinal do valor se necessário
      if ((mapping.positiveOnly && amount !== null && amount <= 0) ||
          (mapping.negativeOnly && amount !== null && amount > 0)) {
        return null;
      }
      
      return {
        patternKey: sicoobKey,
        category: mapping.category,
        categoryPath: mapping.categoryPath,
        positiveOnly: mapping.positiveOnly || false,
        negativeOnly: mapping.negativeOnly || false,
        normalizedDescription: normalizedDescription,
        isSicoobPattern: true
      };
    }
  }
  
  // 3. Primeiro, verificar se é um boleto e tem beneficiário
  const boletoInfo = extractBoletoInfo(description);
  if (boletoInfo && boletoInfo.beneficiary) {
    // Verificar se este beneficiário já está mapeado
    const beneficiaryKey = `beneficiary_${boletoInfo.beneficiary.trim().toLowerCase()}`;
    
    if (registeredBeneficiaries[beneficiaryKey]) {
      const mapping = registeredBeneficiaries[beneficiaryKey];
      
      // Verificar o sinal do valor se necessário
      if ((mapping.positiveOnly && amount !== null && amount <= 0) ||
          (mapping.negativeOnly && amount !== null && amount > 0)) {
        return null;
      }
      
      return {
        patternKey: beneficiaryKey,
        category: mapping.category,
        categoryPath: mapping.categoryPath,
        positiveOnly: mapping.positiveOnly || false,
        negativeOnly: mapping.negativeOnly || false,
        beneficiary: boletoInfo.beneficiary,
        isBeneficiaryPattern: true
      };
    }
  }
  
  // 4. Se não for um boleto mapeado, continuar com a lógica normal de padrões
  const patterns = getPatternsByBank(bankName);
  
  // Verificar primeiro os padrões de alta prioridade
  const highPriorityPatterns = Object.values(patterns).filter(p => p.priority === "high");
  for (const pattern of highPriorityPatterns) {
    // Verificar se o padrão é positiveOnly e o valor é negativo
    if (pattern.positiveOnly && amount !== null && amount <= 0) {
      continue; // Pular este padrão
    }
    
    if (pattern.matcher(normalizedDescription)) {
      return {
        patternKey: pattern.key,
        category: pattern.category,
        categoryPath: pattern.categoryPath,
        positiveOnly: pattern.positiveOnly || false
      };
    }
  }
  
  // Verificar padrões regulares
  const regularPatterns = Object.values(patterns).filter(p => p.priority !== "high");
  for (const pattern of regularPatterns) {
    // Verificar se o padrão é positiveOnly e o valor é negativo
    if (pattern.positiveOnly && amount !== null && amount <= 0) {
      continue; // Pular este padrão
    }
    
    if (pattern.matcher(normalizedDescription)) {
      return {
        patternKey: pattern.key,
        category: pattern.category,
        categoryPath: pattern.categoryPath,
        positiveOnly: pattern.positiveOnly || false
      };
    }
  }
  
  return null;
};